<template>
    <div class="list-group">
        <api-errors :errors="allErrors" />
        <div class="list-group-item"
             v-for="(metadataRepresentation, metadataRepIndex) in listOfMetadataRepresentations" :key="metadataRepIndex"
        >
            <h2 :id="metadataRepresentation.name_plural" class="list-group-item-heading">
                {{ metadataRepresentation.name_plural }}
            </h2>
            <div class="list-group-item-text pb-10" v-for="(elem, index) in metadataRepresentation.mapping" :key="index">
                <i v-if="elem.is_existing" class="fas fa-edit aristotle-green"
                   :title="`${elem.name} is an existing item and will be updated.`"
                />
                <i v-else-if="elem.recently_created" class="fa fa-check aristotle-green"
                   :title="`This ${metadataRepresentation.name_singular} has been successfully saved`"
                />
                <i v-else class="fad fa-plus-square aristotle-font-awesome-duotone"
                   :title="`This ${metadataRepresentation.name_singular} will be created`"
                />
                <strong v-if="elem.is_existing || elem.recently_created" :id="getElementId(elem.name, index)">
                    <a :href="elem.url" target="_blank">{{ elem.name }}</a>
                </strong>
                <strong v-else :id="getElementId(elem.name, index)">
                    {{ elem.name }}
                </strong>
                <div v-if="elem.fields.uuid && fileIsLoaded">
                    <div class="alert alert-info"
                        v-if="elem.fields.uuid.workgroup.id !== selectedWorkGroup && selectedWorkGroup">
                        <div v-if="elem.fields.uuid.workgroup.id === 'noWorkgroup'">
                            This item will be added to <em>{{ workgroupName }}</em>
                        </div>
                        <div v-else>
                            This item will be moved from <em>{{ elem.fields.uuid.workgroup.name }}</em> to <em>{{ workgroupName }}</em>
                        </div>
                    </div>
                </div>
                <ul class="list-group-item-text fields-table">
                    <li v-for="(fieldValue, fieldName, fieldIndex) in elem.fields" :key="fieldIndex" class="mt-10">
                        <div v-if="fieldName === 'permissiblevalue_set' || fieldName === 'supplementaryvalue_set'">
                            <abstract-value-representation :value-name="fieldName" :contents="fieldValue" />
                        </div>
                        <div v-else-if="fieldValue.is_existing">
                            <em>{{ getMetadataTypeNameSingular(fieldName) }}</em>
                            <span>
                            <i class="fas fa-link aristotle-green"
                               :title="`${fieldValue.name} is an existing item.`"
                            />
                            <a :href="fieldValue.url" target="_blank">{{ fieldValue.name }}</a>
                            </span>
                        </div>
                        <div v-else-if="metadataNamesMappingSingular.has(fieldName)">
                            <em>{{ getMetadataTypeNameSingular(fieldName) }}</em> <a :href="`#${fieldValue}0`">{{ fieldValue }}</a>
                        </div>
                        <div v-else-if="fieldValue.is_url">
                            <em>{{ camelCaseToSentenceCase(fieldName) }}</em>
                            <span>
                            <a :href="fieldValue.url" target="_blank">{{ fieldValue.url }}</a>
                            </span>
                        </div>
                        <div v-else-if="fieldName === 'customvalue_set'">
                            <em>Custom Values</em>
                            <div class="container">
                                <table class="table table-striped customvalue-table">
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>System Name</th>
                                            <th>Content</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(value, key, fieldValueIndex) in fieldValue" :key="fieldValueIndex">
                                            <td> {{ getNameFromSystemName(key) }}</td>
                                            <td> {{ key }}</td>
                                            <td> {{ value }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div v-else-if="fieldName === 'identifiers'">
                            <em>Identifiers</em>
                            <div class="container">
                                <table class="table table-striped customvalue-table">
                                    <thead>
                                        <tr>
                                            <th>Namespace</th>
                                            <th>Identifier</th>
                                            <th>Version</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(value, key, fieldValueIndex) in fieldValue" :key="fieldValueIndex">
                                            <td> <a :href="getUrlFromNamespaceIdNameMappingList(value.namespace)" target="_blank">{{ getNameFromNamespaceIdNameMappingList(value.namespace) }}</a></td>
                                            <td> {{ value.identifier }}</td>
                                            <td> {{ value.version }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div v-else-if="fieldName === 'alternative_names'">
                            <em>Alternative Names</em>
                            <div class="container">
                                <table class="table table-striped customvalue-table">
                                    <thead>
                                        <tr>
                                            <th>Alternative Name Type</th>
                                            <th>Alternative Name</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(value, key, fieldValueIndex) in fieldValue" :key="fieldValueIndex">
                                            <td> {{ key }}</td>
                                            <td> {{ value }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div v-else>
                            <em>{{ camelCaseToSentenceCase(fieldName) }}</em>
                            <span>{{ fieldValue }}</span>
                        </div>
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script>
import MetadataNameMappings from './metadata-name-mappings.js'
import AbstractValueRepresentation from "@/sddf/AbstractValueRepresentation.vue"
import { anyCaseToCapitalisedSentenceCase } from 'src/lib/utils.js'
import { getMetadataTypeNameSingular } from '@/fileuploader/utils.js'
import apiRequest from 'src/mixins/api-request.js'
import apiErrors from '@/ApiErrorDisplay.vue'

export default {
    name: "MetadataRepresentation",
    components: {
        AbstractValueRepresentation,
        apiErrors,
    },
    mixins: [apiRequest],
    props: {
        listOfMetadataRepresentations: {
            type: Array,
            required: true,
        },
        namespaceIdNameMappingApiUrl: {
            type: String,
            required: true,
        },
        selectedWorkGroup: {
            type: [Number, null, undefined],
            default: null,
        },
        workgroups: {
            type: Array,
            required: true,
        },
        fileIsLoaded: {
            type: Boolean,
            required: true,
        },
        systemNameToNameMapping: {
            type: Object,
            defualt: {}
        },
    },
    data: () => ({
        metadataNamesMappingSingular: MetadataNameMappings.metadataNamesMappingSingular,
        namespaceIdNameUrlMapping: {},
        namespaceErrors: {},
        requestSent: false,
    }),
    computed: {
        identifiersLength() {
            const firstItem = this.listOfMetadataRepresentations[0]
            if (firstItem?.mapping[0]?.fields?.identifiers) {
                return firstItem.mapping[0].fields.identifiers.length
            }
            return 0
            },
        allErrors() {
            return {
                ...this.namespaceErrors
            }
        },
        workgroupName() {
            const workgroup = this.workgroups.find(group => group.id === this.selectedWorkGroup)
            return workgroup ? workgroup.label : ''
        },
    },
    watch: {
        identifiersLength(newLength) {
            if (!this.requestSent && newLength > 0) {
                const namespaceIdNameMappingApiUrlSpecific = this.namespaceIdNameMappingApiUrl.replace("{pk}", this.selectedWorkGroup )
                let namespaceIdNameUrlMappingPromise = this.get(namespaceIdNameMappingApiUrlSpecific)
                namespaceIdNameUrlMappingPromise.then((response) => {
                    const mapping = {}
                    response.data.forEach((item) => {
                        mapping[item.id] = item
                    })
                    this.namespaceIdNameUrlMapping = mapping
                }).catch((e) => {
                    this.namespaceErrors = { namespaceError: e.message }
                })
                this.requestSent = true
            }
        }
    },
    created() {
    },
    methods: {
        camelCaseToSentenceCase: (text) => {
            return anyCaseToCapitalisedSentenceCase(text)
        },
        getMetadataTypeNameSingular,
        getElementId: (elementName, index) => {
            return `${elementName}${index}`
        },
        getNameFromSystemName: function(key) {
            return this.systemNameToNameMapping[key]
        },
        getNameFromNamespaceIdNameMappingList: function(id) {
            return this.namespaceIdNameUrlMapping[id]?.name ?? "-"
        },
        getUrlFromNamespaceIdNameMappingList: function(id) {
            return this.namespaceIdNameUrlMapping[id]?.url ?? ""
        },
    },
}
</script>

<style scoped>
.mt-10 {
    margin-top: 10px;
}
.pb-10 {
    padding-bottom: 10px;
}
ul.fields-table > li > div > em  {
    width: 20%;
}
ul.fields-table > li > div > em::after  {
    content: ":"
}
ul.fields-table > li > div  {
    display: table;
    width: 100%
}
ul.fields-table > li > div > * {
    display: table-cell;
    padding-right: 5px;
}
.container {
    padding: 0;
    box-sizing: border-box;
    word-break: break-all;

}
.customvalue-table {
    border: 1px solid #ddd;
}
.customvalue-table th {
    padding: 10px 20px;
    white-space: nowrap;
}
.customvalue-table td {
    padding: 10px 20px;
}
</style>
